import React, { useEffect, useMemo, useRef, useState } from 'react';

const sanitizeHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const allowed = ['B', 'STRONG', 'U', 'BR'];

    const sanitizeNode = (node) => {
        const nodeName = node.nodeName.toUpperCase();
        if (node.nodeType === Node.TEXT_NODE) return;
        if (!allowed.includes(nodeName)) {
            const parent = node.parentNode;
            while (node.firstChild) {
                parent.insertBefore(node.firstChild, node);
            }
            parent.removeChild(node);
            return;
        }
        while (node.attributes && node.attributes.length > 0) {
            node.removeAttribute(node.attributes[0].name);
        }
        Array.from(node.childNodes).forEach(sanitizeNode);
    };

    Array.from(doc.body.childNodes).forEach(sanitizeNode);
    return doc.body.innerHTML;
};

const htmlToPlainText = (html = '') => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const NoteEditor = ({
    value = '',
    onChange,
    onSave,
    readOnly = false,
    variant = 'default',
    label = 'Notizen (nur Moderator)',
}) => {
    const [html, setHtml] = useState(sanitizeHtml(value || ''));
    const [status, setStatus] = useState('saved');
    const saveTimeout = useRef(null);
    const editorRef = useRef(null);
    const isFocusedRef = useRef(false);

    useEffect(() => {
        const sanitized = sanitizeHtml(value || '');
        if (!isFocusedRef.current && editorRef.current) {
            editorRef.current.innerHTML = sanitized;
        }
        if (!isFocusedRef.current) {
            setHtml(sanitized);
        }
    }, [value]);

    useEffect(() => {
        if (readOnly) return undefined;
        if (status !== 'unsaved') return undefined;

        saveTimeout.current = setTimeout(async () => {
            setStatus('saving');
            try {
                await onSave?.(html);
                setStatus('saved');
            } catch (error) {
                console.error('Fehler beim Speichern der Notiz', error);
                setStatus('unsaved');
            }
        }, 800);

        return () => {
            if (saveTimeout.current) clearTimeout(saveTimeout.current);
        };
    }, [html, onSave, readOnly, status]);

    useEffect(() => () => {
        if (saveTimeout.current) clearTimeout(saveTimeout.current);
    }, []);

    const syncFromDom = () => {
        if (!editorRef.current) return;
        const nextHtml = sanitizeHtml(editorRef.current.innerHTML);
        setHtml(nextHtml);
        setStatus('unsaved');
        onChange?.(nextHtml);
    };

    const applyFormat = (command) => {
        if (readOnly) return;
        document.execCommand(command, false, null);
        syncFromDom();
    };

    const handleKeyDown = (event) => {
        const isCmd = event.metaKey || event.ctrlKey;
        if (!isCmd) return;

        if (event.key.toLowerCase() === 'b') {
            event.preventDefault();
            applyFormat('bold');
        }
        if (event.key.toLowerCase() === 'u') {
            event.preventDefault();
            applyFormat('underline');
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const text = event.clipboardData.getData('text/plain');
        const htmlText = text.replace(/\n/g, '<br>');
        document.execCommand('insertHTML', false, sanitizeHtml(htmlText));
        syncFromDom();
    };

    const handleFocus = () => {
        isFocusedRef.current = true;
    };

    const handleBlur = () => {
        isFocusedRef.current = false;
    };

    const handleInput = () => {
        syncFromDom();
    };

    const preview = useMemo(() => htmlToPlainText(html), [html]);

    const statusLabel = {
        saved: 'Saved',
        unsaved: 'Unsaved',
        saving: 'Saving',
    }[status];

    if (variant === 'compact') {
        return (
            <div className="note-editor note-editor-compact">
                <div className="note-editor-header">
                    <span className="note-label">{label}</span>
                    <span className={`note-status ${status}`}>{statusLabel}</span>
                </div>
                <div className="note-preview" title={preview || 'Keine Notiz'}>
                    {preview || 'Keine Notiz'}
                </div>
            </div>
        );
    }

    return (
        <div className="note-editor">
            <div className="note-editor-header">
                <span className="note-label">{label}</span>
                <span className={`note-status ${status}`}>{statusLabel}</span>
            </div>
            <div className="note-toolbar">
                <button type="button" onClick={() => applyFormat('bold')} disabled={readOnly}>
                    Bold
                </button>
                <button type="button" onClick={() => applyFormat('underline')} disabled={readOnly}>
                    Underline
                </button>
            </div>
            <div
                className="note-editor-area"
                contentEditable={!readOnly}
                suppressContentEditableWarning
                ref={editorRef}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
        </div>
    );
};

export default NoteEditor;
